import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import type { DashboardData, NotaSimplificada, StatusSummary } from "./fiscal-types";
import { fmtBRL, fmtNum } from "./format";

const MARGIN = 40;

type RGB = [number, number, number];

// Glass Blue Pro — LIGHT UI Palette
const CLR_BG: RGB = [252, 253, 255];
const CLR_TEXT: RGB = [11, 31, 77];
const CLR_PRIMARY: RGB = [26, 102, 204];
const CLR_GLOW: RGB = [51, 204, 255];
const CLR_MUTED: RGB = [100, 130, 180];

const CLR_SUCCESS: RGB = [16, 185, 129];
const CLR_WARNING: RGB = [245, 158, 11];
const CLR_DANGER: RGB = [239, 68, 68];

function drawGlassCard(
  doc: jsPDF,
  x: number,
  y: number,
  w: number,
  h: number,
  label: string,
  value: string,
  sub: string | undefined,
  baseColor: RGB,
) {
  doc.setFillColor(240, 245, 255);
  doc.roundedRect(x + 2, y + 2, w, h, 8, 8, "F");
  doc.setFillColor(255, 255, 255);
  doc.roundedRect(x, y, w, h, 8, 8, "F");
  doc.setDrawColor(210, 225, 250);
  doc.setLineWidth(0.8);
  doc.roundedRect(x, y, w, h, 8, 8, "D");
  doc.setFillColor(baseColor[0], baseColor[1], baseColor[2]);
  doc.roundedRect(x, y, 4, h, 2, 2, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(7);
  doc.setTextColor(CLR_MUTED[0], CLR_MUTED[1], CLR_MUTED[2]);
  doc.text(label.toUpperCase(), x + 12, y + 16);
  doc.setFontSize(14);
  doc.setTextColor(CLR_TEXT[0], CLR_TEXT[1], CLR_TEXT[2]);
  doc.text(value, x + 12, y + 36);
  if (sub) {
    doc.setFontSize(6.5);
    doc.setTextColor(CLR_PRIMARY[0], CLR_PRIMARY[1], CLR_PRIMARY[2]);
    doc.text(sub, x + 12, y + 50);
  }
}

function drawSummaryTaxCard(doc: jsPDF, x: number, y: number, w: number, h: number, label: string, value: string) {
  // Header Azul
  doc.setFillColor(CLR_PRIMARY[0], CLR_PRIMARY[1], CLR_PRIMARY[2]);
  doc.roundedRect(x, y, w, h * 0.4, 4, 4, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.setTextColor(255, 255, 255);
  doc.text(label.toUpperCase(), x + w / 2, y + h * 0.25, { align: "center" });

  // Corpo Branco
  doc.setFillColor(255, 255, 255);
  doc.setDrawColor(CLR_PRIMARY[0], CLR_PRIMARY[1], CLR_PRIMARY[2]);
  doc.roundedRect(x, y + h * 0.4, w, h * 0.6, 4, 4, "FD");
  doc.setFontSize(11);
  doc.setTextColor(CLR_TEXT[0], CLR_TEXT[1], CLR_TEXT[2]);
  doc.text(value, x + w / 2, y + h * 0.8, { align: "center" });
}

export function exportDashboardPdf(
  dashboard: DashboardData,
  notas: NotaSimplificada[],
  duracaoMs: number,
) {
  const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();

  const renderHeader = (title: string, sub: string) => {
    doc.setFillColor(CLR_BG[0], CLR_BG[1], CLR_BG[2]);
    doc.rect(0, 0, pageW, pageH, "F");
    doc.setFillColor(CLR_PRIMARY[0], CLR_PRIMARY[1], CLR_PRIMARY[2]);
    doc.rect(0, 0, pageW, 40, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.setTextColor(255, 255, 255);
    doc.text(title, MARGIN, 25);
    doc.setFontSize(8);
    doc.setTextColor(200, 230, 255);
    doc.text(sub, pageW - MARGIN, 25, { align: "right" });
  };

  // PAG 1: DASHBOARD
  renderHeader("DASHBOARD FISCAL ESTRATÉGICO", `Gerado em ${new Date().toLocaleString()}`);
  let cursorY = 70;
  
  const cardW = (pageW - MARGIN * 2 - 30) / 4;
  const { statusSummary } = dashboard;
  drawGlassCard(doc, MARGIN, cursorY, cardW, 60, "Autorizadas", fmtNum(statusSummary.autorizadas.quantidade), fmtBRL(statusSummary.autorizadas.valorTotal), CLR_SUCCESS);
  drawGlassCard(doc, MARGIN + (cardW + 10), cursorY, cardW, 60, "Aut. em Contingência", fmtNum(statusSummary.contingencia.quantidade), fmtBRL(statusSummary.contingencia.valorTotal), CLR_WARNING);
  drawGlassCard(doc, MARGIN + 2 * (cardW + 10), cursorY, cardW, 60, "Inutilizadas", fmtNum(statusSummary.inutilizadas.quantidade), "Sem efeito", CLR_MUTED);
  drawGlassCard(doc, MARGIN + 3 * (cardW + 10), cursorY, cardW, 60, "Canceladas", fmtNum(statusSummary.canceladas.quantidade), "Sem efeito", CLR_DANGER);
  
  cursorY += 90;
  doc.setFontSize(11);
  doc.setTextColor(CLR_TEXT[0], CLR_TEXT[1], CLR_TEXT[2]);
  doc.text("CONSOLIDAÇÃO DE TRIBUTOS", MARGIN, cursorY);
  cursorY += 15;

  const tCols = 5;
  const tW = (pageW - MARGIN * 2 - (tCols - 1) * 10) / tCols;
  dashboard.taxCards.forEach((t, i) => {
    const r = Math.floor(i / tCols);
    const c = i % tCols;
    drawGlassCard(doc, MARGIN + c * (tW + 10), cursorY + r * 65, tW, 55, t.title, fmtBRL(t.totalValue), `Base: ${fmtBRL(t.totalBase)}`, CLR_PRIMARY);
  });

  // PAG 2: LISTAGEM E TOTAIS (PÁGINA FINAL CONFORME SOLICITADO)
  doc.addPage();
  doc.setFillColor(CLR_BG[0], CLR_BG[1], CLR_BG[2]);
  doc.rect(0, 0, pageW, pageH, "F");

  doc.setFont("helvetica", "bold");
  doc.setFontSize(18);
  doc.setTextColor(CLR_TEXT[0], CLR_TEXT[1], CLR_TEXT[2]);
  doc.text("Listagem de Notas Processadas", MARGIN, 40);
  doc.setFontSize(9);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(120, 120, 120);
  doc.text(`${notas.length} notas — impostos por coluna · Totalizações consideram apenas Autorizadas + Aut. em Contingência`, MARGIN, 55);

  const isValid = (n: NotaSimplificada) => n.notaStatus === "AUTORIZADA" || n.notaStatus === "CONTINGENCIA";
  const validas = notas.filter(isValid);
  
  const taxTotals = validas.reduce((acc, n) => {
    acc.vNF += n.valorOficial || 0;
    acc.icms += n.vICMS || 0;
    acc.fecp += n.vFCP || 0;
    acc.icmsSt += n.vST || 0;
    acc.fecpSt += n.vFCPST || 0;
    acc.pis += n.vPIS || 0;
    acc.cofins += n.vCOFINS || 0;
    acc.ipi += n.vIPI || 0;
    acc.ibs += n.vIBS || 0;
    acc.cbs += n.vCBS || 0;
    return acc;
  }, { vNF: 0, icms: 0, fecp: 0, icmsSt: 0, fecpSt: 0, pis: 0, cofins: 0, ipi: 0, ibs: 0, cbs: 0 });

  autoTable(doc, {
    startY: 65,
    head: [[ "Nº NF", "Emitente", "Data", "Status", "Valor Nota", "ICMS", "FECP", "ICMS-ST", "FECP-ST", "PIS", "COFINS", "IPI", "IBS", "CBS" ]],
    body: notas.map(n => [
      n.numero, (n.emitente || "").slice(0, 20), n.emissao?.split('.')[0] || "", n.notaStatus, 
      fmtBRL(n.valorOficial), fmtBRL(n.vICMS), fmtBRL(n.vFCP), fmtBRL(n.vST), fmtBRL(n.vFCPST),
      fmtBRL(n.vPIS), fmtBRL(n.vCOFINS), fmtBRL(n.vIPI), fmtBRL(n.vIBS), fmtBRL(n.vCBS)
    ]),
    theme: 'grid',
    styles: { fontSize: 6.5, cellPadding: 3, halign: "center" },
    headStyles: { fillColor: CLR_PRIMARY, textColor: 255 },
    columnStyles: { 1: { halign: "left" } },
    margin: { left: MARGIN, right: MARGIN }
  });

  let finalY = (doc as any).lastAutoTable.finalY + 30;
  doc.setDrawColor(CLR_PRIMARY[0], CLR_PRIMARY[1], CLR_PRIMARY[2]);
  doc.setLineWidth(1);
  doc.line(MARGIN, finalY, pageW - MARGIN, finalY);
  finalY += 20;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(14);
  doc.setTextColor(CLR_PRIMARY[0], CLR_PRIMARY[1], CLR_PRIMARY[2]);
  doc.text("Totalização por imposto", MARGIN, finalY);
  finalY += 12;
  doc.setFontSize(8);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(100, 100, 100);
  doc.text(`Considera apenas notas AUTORIZADAS + AUT. EM CONTINGÊNCIA (${validas.length} de ${notas.length})`, MARGIN, finalY);
  finalY += 20;

  const taxList = [
    { l: "TOTAL ICMS", v: taxTotals.icms },
    { l: "TOTAL FECP", v: taxTotals.fecp },
    { l: "TOTAL ICMS-ST", v: taxTotals.icmsSt },
    { l: "TOTAL FECP-ST", v: taxTotals.fecpSt },
    { l: "TOTAL PIS", v: taxTotals.pis },
    { l: "TOTAL COFINS", v: taxTotals.cofins },
    { l: "TOTAL IPI", v: taxTotals.ipi },
    { l: "TOTAL IBS", v: taxTotals.ibs },
    { l: "TOTAL CBS", v: taxTotals.cbs },
  ];

  const gridCols = 5;
  const gW = (pageW - MARGIN * 2 - (gridCols - 1) * 10) / gridCols;
  const gH = 40;
  taxList.forEach((tax, i) => {
    const row = Math.floor(i / gridCols);
    const col = i % gridCols;
    drawSummaryTaxCard(doc, MARGIN + col * (gW + 10), finalY + row * (gH + 15), gW, gH, tax.l, fmtBRL(tax.v));
  });

  const barY = finalY + 2 * (gH + 15) + 20;
  doc.setFillColor(CLR_PRIMARY[0], CLR_PRIMARY[1], CLR_PRIMARY[2]);
  doc.roundedRect(MARGIN, barY, pageW - MARGIN * 2, 40, 4, 4, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.setTextColor(255, 255, 255);
  doc.text("TOTAL GERAL DAS NOTAS (Autorizadas + Aut. em Contingência)", MARGIN + 20, barY + 25);
  doc.setFontSize(14);
  doc.text(fmtBRL(taxTotals.vNF), pageW - MARGIN - 20, barY + 25, { align: "right" });

  // Adicionar Rodapé de Isenção (Legal Disclaimer) em todas as páginas
  const totalPages = (doc as any).internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFont("helvetica", "italic");
    doc.setFontSize(7);
    doc.setTextColor(150, 150, 150);
    const disclaimer = "DOCUMENTO AUXILIAR E ESTATÍSTICO: Este relatório não substitui o trabalho, parecer técnico ou assinatura de um Profissional Contábil (CRC). A tomada de decisões tributárias é de inteira responsabilidade da empresa e deve ser validada por seu contador.";
    doc.text(disclaimer, pageW / 2, pageH - 15, { align: "center" });
  }

  doc.save(`extrator-fiscal-report-${new Date().toISOString().slice(0, 10)}.pdf`);
}
